import ConfirmationPanel from 'components/form/confirmation/confirmation-panel';
import { ApplicationManagerClient } from 'clients/application-manager-client';
import { IGetServiceGroupsResponseItem } from 'models/application-manager/users/IGetServiceGroupsResponse';
import React from 'react';
import Validation from 'components/form/validation/validation';
import { Controller, useForm } from 'react-hook-form';
import { Grid } from '@mui/material';
import { WithStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import { nameOfFactory } from 'utils/types-util';
import { ReactSelect } from 'components/form/field/react_select';
import { NumberOption } from 'interfaces/forms/types';
import { LicensedStatesTypeFormStyles } from './licensed-states-form.styles';
import { IFormFields } from './types';
import { logger } from '../../../winston-logger';

type ServiceGroupTypeFormFields = IFormFields<NumberOption[]>;

interface IServiceGroupFormProps extends WithStyles<typeof LicensedStatesTypeFormStyles> {
  initialValue: number[];
  customerId: number;
  handleCancel: () => void;
  handleSubmit: (value: number[]) => void;
}

const getFieldName = nameOfFactory<ServiceGroupTypeFormFields>();

const ServiceGroupForm: React.FC<IServiceGroupFormProps> = (
  props: IServiceGroupFormProps,
): JSX.Element => {
  const { classes, customerId } = props;

  // #region component state
  const {
    handleSubmit,
    control,
    setError,

    formState: { errors },
  } = useForm<any>();
  const [serviceGroups, setServiceGroups] = React.useState<any[]>([]);
  const [defaultValue, setDefaultValue] = React.useState<any[]>([]);

  React.useEffect(() => {
    (async () => {
      // Load service groups
      try {
        const apiResponse = await ApplicationManagerClient.getCustomerServiceGroups(customerId);

        if (apiResponse.data) {
          const response: IGetServiceGroupsResponseItem[] = apiResponse.data;
          setServiceGroups(
            response.map(customerServiceGroup => ({
              label: customerServiceGroup.display_name,
              value: customerServiceGroup.id,
            })),
          );
        }
      } catch (error) {
        logger.error(error);
      }
    })();
  }, [customerId]);

  React.useEffect(() => {
    setDefaultValue(serviceGroups.filter(x => props.initialValue.includes(x.value)));
  }, [serviceGroups, props.initialValue]);
  // #endregion

  // #region helpers
  const onSubmit = (formValues: ServiceGroupTypeFormFields) => {
    if (formValues.selectedValue.length === 0) {
      setError(getFieldName('selectedValue'), { type: 'manual' });
    } else {
      const formattedValue = formValues.selectedValue.map(x => x.value);

      props.handleSubmit(formattedValue);
    }
  };

  const onCancel = () => {
    props.handleCancel();
  };

  const getDefaultValue = (): NumberOption[] => {
    return defaultValue;
  };
  // #endregion

  return (
    <Grid container direction="column" className={classes.container}>
      <Grid item>
        <form>
          <Controller
            control={control}
            name={getFieldName('selectedValue')}
            rules={{ required: true }}
            defaultValue={getDefaultValue()}
            render={(fieldProps: any) => (
              <>
                <ReactSelect
                  name={getFieldName('selectedValue')}
                  value={fieldProps?.field?.value}
                  fields={serviceGroups}
                  menuPosition="relative"
                  handleOnChange={fieldProps?.field?.onChange}
                  isMulti
                />
                <Validation touched={errors.selectedValue} error="Required" />
              </>
            )}
          />
        </form>
      </Grid>
      <Grid item>
        <ConfirmationPanel
          handleSubmit={handleSubmit(onSubmit)}
          handleCancel={() => {
            onCancel();
          }}
        />
      </Grid>
    </Grid>
  );
};

export const ServiceGroupFormStyled = withStyles(LicensedStatesTypeFormStyles)(ServiceGroupForm);
